import {
  ELEVAR_COOKIES_KEY,
  PASSED_DISCOUNT_KEY,
  UTM_PARAMS_KEY,
  VISITOR_INFO_KEY,
} from "@constants/storageKeys"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import * as React from "react"

type Opts = {
  runOnce?: boolean
  search?: string
}

export default function useBootstrapClientStorage({
  runOnce = true,
  search,
}: Opts) {
  const ranRef = React.useRef(false)

  React.useEffect(() => {
    if (runOnce && ranRef.current) {
      return
    }

    if (typeof window === "undefined") {
      return
    }

    // ----- URL params -----
    const params = new URLSearchParams(search ?? window.location.search)

    // UTM params
    const utmParams = {
      utm_campaign: params.get("utm_campaign"),
      utm_content: params.get("utm_content"),
      utm_medium: params.get("utm_medium"),
      utm_source: params.get("utm_source"),
      utm_term: params.get("utm_term"),
    }
    const storedUTM = getLocalStorage(UTM_PARAMS_KEY) || {}
    const utmToUpdate = Object.entries(utmParams).filter(([, v]) => !!v)
    if (utmToUpdate.length) {
      const updated = { ...storedUTM }
      utmToUpdate?.forEach(([k, v]) => (updated[k] = v))
      writeLocalStorage(UTM_PARAMS_KEY, updated)
    }

    // Passed coupon
    const coupon = params.get("coupon")
    if (coupon) {
      writeLocalStorage(PASSED_DISCOUNT_KEY, coupon)
    }

    // Visitor info (fbclid/gclid + UTM again for convenience)
    const visitorInfo = {
      fbclid: params.get("fbclid"),
      gclid: params.get("gclid"),
      ...utmParams,
    }
    const storedVisitor = getLocalStorage(VISITOR_INFO_KEY) || {}
    const visitorToUpdate = Object.entries(visitorInfo).filter(([, v]) => !!v)
    if (visitorToUpdate.length) {
      const updated = { ...storedVisitor }
      visitorToUpdate?.forEach(([k, v]) => (updated[k] = v))
      writeLocalStorage(VISITOR_INFO_KEY, updated)
    }

    // ----- Elevar cookies -----
    const w = window as any
    const setElevarCookies = async () => {
      let _ga: null | string = null
      let _fbp: null | string = null
      let _fbc: null | string = null

      if (w.cookieStore?.get) {
        const [ga, fbp, fbc] = await Promise.all([
          w.cookieStore.get("_ga"),
          w.cookieStore.get("_fbp"),
          w.cookieStore.get("_fbc"),
        ])
        _ga = ga?.value ?? null
        _fbp = fbp?.value ?? null
        _fbc = fbc?.value ?? null
      } else {
        _ga = readCookie("_ga")
        _fbp = readCookie("_fbp")
        _fbc = readCookie("_fbc")
      }

      const existing = getLocalStorage(ELEVAR_COOKIES_KEY) || {}
      const next = { ...existing }
      if (_ga) {
        next._elevar__ga = _ga
      }
      if (_fbp) {
        next._elevar__fbp = _fbp
      }
      if (_fbc) {
        next._elevar__fbc = _fbc
      }
      writeLocalStorage(ELEVAR_COOKIES_KEY, next)
    }

    setElevarCookies()
    ranRef.current = true
  }, [runOnce]) // deliberately ignore `search` if runOnce=true
}

// Fallback for browsers without cookieStore
function readCookie(name: string): null | string {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}
